import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/** Admin → Academy tab: content approval, member verification, grading queue, certificates, hiring. */
export function AcademyTab() {
  const [summary, setSummary] = useState(null);
  const [written, setWritten] = useState([]);
  const [members, setMembers] = useState([]);
  const [careers, setCareers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [grades, setGrades] = useState({});
  const [issue, setIssue] = useState({ memberId: "", kind: "belt:white", signatureName: "" });
  const [msg, setMsg] = useState("");

  async function reload() {
    const [s, w, m, c, f] = await Promise.all([
      api.get("/academy/admin/summary").then((r) => r.data),
      api.get("/academy/admin/written?status=submitted").then((r) => r.data.items),
      api.get("/academy/admin/members").then((r) => r.data.members),
      api.get("/academy/admin/careers").then((r) => r.data.items),
      api.get("/academy/admin/feedback").then((r) => r.data.items),
    ]);
    setSummary(s);
    setWritten(w);
    setMembers(m);
    setCareers(c);
    setFeedback(f);
  }
  useEffect(() => { reload().catch(() => {}); }, []);

  return (
    <div data-testid="admin-academy-tab">
      <div className="admin-actions">
        <button className="btn small" data-testid="admin-academy-approve-all" onClick={async () => {
          await api.post("/academy/admin/content/approve-all");
          setMsg("All lessons, questions, labs and belt standards approved for publication (180-day review due date set).");
          reload();
        }}>Approve all academy content for publication</button>
        <button className="btn small" onClick={reload}>Refresh</button>
        {msg && <span className="micro">{msg}</span>}
      </div>
      {summary && (
        <p className="micro" data-testid="admin-academy-summary">
          {summary.members} members · {summary.publishedLessons}/{summary.totalLessons} lessons published ·
          {" "}{summary.pendingWritten} written tasks pending · {summary.pendingLabs} labs/capstones pending ·
          {" "}{summary.attempts} exams submitted · {summary.certificates} certificates · {summary.careers} applications
        </p>
      )}

      <h3 style={{ marginTop: 18 }}>Grading queue (written tasks, labs, capstones)</h3>
      {written.length === 0 && <p className="micro">Nothing waiting for review.</p>}
      {written.map((w) => (
        <div key={w.id} className="admin-card" data-testid={`admin-academy-written-${w.id}`}>
          <p className="micro"><strong>{w.memberEmail}</strong> · {w.kind} · {w.refId} · submitted {w.createdAt?.slice(0, 10)}</p>
          <p style={{ fontSize: 13 }}>{w.text?.slice(0, 400)}</p>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input type="number" min="0" max="100" step="0.5" placeholder="Score" value={grades[w.id]?.score ?? ""}
              onChange={(e) => setGrades({ ...grades, [w.id]: { ...grades[w.id], score: e.target.value } })}
              style={{ width: 90 }} data-testid={`admin-academy-score-${w.id}`} />
            <input placeholder="Feedback for the student" value={grades[w.id]?.feedback ?? ""}
              onChange={(e) => setGrades({ ...grades, [w.id]: { ...grades[w.id], feedback: e.target.value } })}
              style={{ flex: 1, minWidth: 200 }} />
            <button className="btn small" onClick={async () => {
              await api.post(`/academy/admin/written/${w.id}/grade`, { score: Number(grades[w.id]?.score), feedback: grades[w.id]?.feedback || "" });
              reload();
            }}>Save grade</button>
          </div>
          <p className="micro">Pass marks: written 8/10, labs/capstone 80/100.</p>
        </div>
      ))}

      <h3 style={{ marginTop: 22 }}>Members</h3>
      {members.map((m) => (
        <div key={m.id} className="admin-card" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ flex: 1, minWidth: 180 }}>{m.displayName} · {m.email} {m.emailVerified ? "· ✓ verified" : "· unverified"}</span>
          {!m.emailVerified && (
            <button className="btn small" data-testid={`admin-academy-verify-${m.id}`} onClick={async () => { await api.post(`/academy/admin/members/${m.id}/verify`); reload(); }}>
              Mark verified
            </button>
          )}
        </div>
      ))}

      <h3 style={{ marginTop: 22 }}>Issue a certificate (server-verified eligibility)</h3>
      <div className="admin-card" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select value={issue.memberId} onChange={(e) => setIssue({ ...issue, memberId: e.target.value })} style={{ minWidth: 200 }}>
          <option value="">Choose member…</option>
          {members.map((m) => <option key={m.id} value={m.id}>{m.displayName} — {m.email}</option>)}
        </select>
        <select value={issue.kind} onChange={(e) => setIssue({ ...issue, kind: e.target.value })}>
          {["belt:white", "belt:yellow", "belt:orange", "belt:green", "belt:blue", "belt:purple", "belt:brown", "belt:black", "powerwall"].map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
        <input placeholder="Authorised issuer signature name" value={issue.signatureName} onChange={(e) => setIssue({ ...issue, signatureName: e.target.value })} />
        <button className="btn small lime" data-testid="admin-academy-issue" onClick={async () => {
          try {
            const res = await api.post("/academy/admin/certificates/issue", issue);
            setMsg(`Issued — verification link: /academy/verify/${res.data.publicToken}`);
            reload();
          } catch (e2) {
            setMsg(e2.response?.data?.detail || "Issuance failed — eligibility not met.");
          }
        }}>Issue</button>
      </div>

      <h3 style={{ marginTop: 22 }}>Hiring pipeline</h3>
      {careers.length === 0 && <p className="micro">No applications yet.</p>}
      {careers.map((c) => (
        <div key={c.id} className="admin-card" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ flex: 1, minWidth: 220 }}>{c.displayName} · {c.roleInterest} · {c.status}</span>
          {["review", "interview", "hired", "declined"].map((s) => (
            <button key={s} className="btn small" onClick={async () => { await api.post(`/academy/admin/careers/${c.id}/status`, { status: s }); reload(); }}>{s}</button>
          ))}
        </div>
      ))}

      <h3 style={{ marginTop: 22 }}>Tester suggestions</h3>
      {feedback.length === 0 && <p className="micro" data-testid="admin-academy-feedback-empty">No suggestions yet.</p>}
      {feedback.map((f) => (
        <div key={f.id} className="admin-card" data-testid={`admin-academy-feedback-${f.id}`}>
          <p className="micro"><strong>{f.reference}</strong> · {f.email || "anonymous"} · {f.page} · {f.createdAt?.slice(0, 10)} · status: {f.status}</p>
          <p style={{ fontSize: 13 }}>{f.text}</p>
          <div style={{ display: "flex", gap: 8 }}>
            {["noted", "done"].map((s) => (
              <button key={s} className="btn small" onClick={async () => { await api.post(`/academy/admin/feedback/${f.id}/status`, { status: s }); reload(); }}>Mark {s}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
